// Copied verbatim from login-farmacias/contract/ts/lf-claims.ts (contract-v1.6). Do not edit:
// re-copy from the contract if the token contract changes. The C# LfClaims in bidscircleapi and
// Track A's OrgsCodec decode the same `anf|lab|roles|perms` format.

export interface LfOrg { anf: string | null; lab: string | null; roles: string[]; perms: string[]; }

/** Entra serialises a one-element array claim as a plain string and a longer one as an array. */
export type ClaimList = string | string[] | undefined;

export interface LfClaims {
  /* lf_roles: global_admin, company_admin:ezfy, company_admin:bidscircle, company_admin:amily (v1.5). */
  lf_v?: string; lf_roles?: ClaimList; orgs?: ClaimList; orgs_overflow?: string;
  home_company?: string; tc_required?: string; tc_pending?: string; tc_version?: string; ezfy_profile_id?: string;
  /** ANF digits of the person's main pharmacy; absent when none is marked. A default, never a grant. */
  main_anf?: string;
  /** The person's employee number, digits; absent when unknown. Data about the person, never a grant. */
  employee_id?: string;
}

const split = (s: string) => (s ? s.split(',').filter(Boolean) : []);

/** Both shapes a multi-valued claim can arrive in, as one list. */
export const asList = (v: ClaimList): string[] => (v == null ? [] : Array.isArray(v) ? v : [v]);

export function parseOrgs(orgs: ClaimList): LfOrg[] {
  return asList(orgs).map((e) => {
    const [anf = '', lab = '', roles = '', perms = ''] = e.split('|');
    return { anf: anf || null, lab: lab || null, roles: split(roles), perms: split(perms) };
  });
}
export const hasPerm = (c: LfClaims, perm: string) => parseOrgs(c.orgs).some((o) => o.perms.includes(perm));
export const hasAccess = (c: LfClaims, app: string) => hasPerm(c, `${app}:access`);
export const hasRole = (c: LfClaims, role: string) => asList(c.lf_roles).includes(role);
export const tcPending = (c: LfClaims) => c.tc_pending === 'true';
export const orgsOverflow = (c: LfClaims) => c.orgs_overflow === 'true';

/** The portal's applications, by permission key, in the order the portal lists them. */
export const APP_KEYS = ['amily', 'ezfyweb', 'bidsweb', 'bidsops'] as const;
export type AppKey = (typeof APP_KEYS)[number];

/**
 * Whether the person may use the portal's administration, as the portal decides it (`isAdmin`): an admin role in
 * `lf_roles` (`global_admin`, `company_admin:*`), or `pharmacy_admin` on an org entry. Such a person keeps
 * "Aplicações" with a single application, since the portal is where they add a user (product owner, 2026-09-30).
 */
export const administersInPortal = (roles: readonly string[], orgs: readonly { roles: readonly string[] }[]): boolean =>
  roles.some((r) => r === 'global_admin' || r.startsWith('company_admin:')) || orgs.some((o) => o.roles.includes('pharmacy_admin'));

/**
 * The portal's applications some org entry grants `<key>:access` for, in `APP_KEYS` order. An
 * application shows its "Aplicações" switcher when this has two or more (app-switcher D4). Takes the
 * claims, or the org list `GET /me` returns when `orgs_overflow` truncated the token's. Withheld
 * permissions (`tc_pending`) are not in either, so they do not count.
 */
export const appsWithAccess = (source: LfClaims | readonly { perms: readonly string[] }[]): AppKey[] => {
  const orgs = Array.isArray(source) ? source : parseOrgs((source as LfClaims).orgs);
  const perms = new Set(orgs.flatMap((o) => o.perms));
  return APP_KEYS.filter((k) => perms.has(`${k}:access`));
};

/**
 * The person's main pharmacy as a number, but only when its org entry carries `perm` (any entry when
 * `perm` is omitted). A default, never a grant (contract rule 6): a main pharmacy the person holds no
 * `<app>:access` on is a correct claim and a wrong default for that app, so it is `null` here and the
 * caller falls back to whatever it did before -- typically the first entitled org.
 */
export const mainAnf = (c: LfClaims, perm?: string): number | null => {
  if (!c.main_anf) return null;
  const org = parseOrgs(c.orgs).find((o) => o.anf === c.main_anf);
  if (!org || (perm !== undefined && !org.perms.includes(perm))) return null;
  const n = Number(c.main_anf);
  return Number.isInteger(n) ? n : null;
};
